import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { GlobalHeader } from "./GlobalHeader";
import "@/pages/marketing/marketing.css";

// Vertical offset (px) to clear the fixed GlobalHeader when scrolling to a
// hash-anchored section, including the candidate-site announcement strip.
const HEADER_OFFSET = 112;

/**
 * MarketingLayout
 *
 * Layout wrapper for public/marketing pages (home, pricing, changelog).
 * Shows the GlobalHeader with marketing navigation and CTA buttons.
 *
 * Also handles scroll-to-hash navigation: any `/#section-id` link (footer,
 * header, etc.) will smooth-scroll to the matching element after the route
 * mounts. Uses a short rAF retry so the section can finish mounting before
 * we measure it.
 */
export const MarketingLayout: React.FC = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      // No anchor: jump to top on plain route changes (matches default browser behavior).
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    const id = hash.replace(/^#/, "");
    if (!id) return;

    // Sections may still be mounting (e.g. lazy/animated entries), so retry a
    // few rAF ticks before giving up.
    let attempts = 0;
    let raf = 0;

    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        window.scrollTo({ top, behavior: reduceMotion ? "auto" : "smooth" });
        return;
      }
      if (attempts++ < 20) {
        raf = window.requestAnimationFrame(tryScroll);
      }
    };

    raf = window.requestAnimationFrame(tryScroll);
    return () => window.cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return (
    <>
      <GlobalHeader />
      <main className="min-h-screen bg-background pt-16">
        <Outlet />
      </main>
    </>
  );
};

export default MarketingLayout;
